import { generateClient } from 'aws-amplify/api';
// @ts-ignore
import config from '../../config/app.json';
import {
  ContentShare,
  CustomDeckDependencies, CustomValues, ShareNotificationScope,
  ShareType,
} from '@alucio/aws-beacon-amplify/src/models'
import {
  GenerateContentShareMutation,
  GenerateContentShareMutationVariables,
  SHARE_TYPE,
} from '@alucio/aws-beacon-amplify/src/API';
import format from 'date-fns/format';
import { store } from 'src/state/redux';
import { currentUserORM } from 'src/state/redux/selector/user';
import { generateContentShare } from '@alucio/aws-beacon-amplify/src/graphql/mutations';
import { selContentSharesByContentIdAndType } from 'src/state/redux/selector/contentShare';
import { contentShareActions } from 'src/state/redux/slice/contentShare';
import {
  getCurrentSharePageSettings,
  matchesSharePageSettings,
} from 'src/utils/shareLink/pageSettings';

export interface ShareableLinkResult {
  contentId: string,
  expiresAt: string,
  id: string,
  link: string,
  title: string,
  type: string,
  customValues: CustomValues[],
  notificationScope: ShareNotificationScope,
}

const isExpiredShare = (expiresAt: string) => {
  const expirationTime = Date.parse(expiresAt)

  return Number.isNaN(expirationTime) || expirationTime <= Date.now()
}

async function getShareableLink(
  contentId: string,
  type: ShareType,
  title: string = '',
  customValues: CustomValues[],
  notificationScope: ShareNotificationScope,
  meetingId?: string,
  customDeckDependencies?: CustomDeckDependencies,
):
  Promise<ShareableLinkResult> {
  const matchesCustomValues = (a: CustomValues[], b: CustomValues[]) =>
    a.length === b.length && a.every(aVal => {
      const bVal = b.find(v => v.fieldId === aVal.fieldId)
      return bVal && aVal.values.length === bVal.values.length && aVal.values.every(v => bVal.values.includes(v))
    })

  const matchesCustomDeckDeps = (a?: CustomDeckDependencies, b?: CustomDeckDependencies) =>
    (!a && !b) || (!!a && !!b && a.folderId === b.folderId &&
      a.versions.length === b.versions.length && a.versions.every(v => b.versions.includes(v)))

  const state = store.getState()
  const currentPageSettings = getCurrentSharePageSettings(state, type, contentId);
  const candidateShares = selContentSharesByContentIdAndType(state, contentId, type)

  const existingShare = candidateShares.length === 0 ? undefined : candidateShares
    .filter(share =>
      !share.revoked &&
      !isExpiredShare(share.expiresAt) &&
      (share.meetingId || undefined) === (meetingId || undefined) &&
      share.notificationScope === notificationScope &&
      matchesCustomValues(share.customValues, customValues) &&
      matchesCustomDeckDeps(share.customDeckDependencies, customDeckDependencies) &&
      matchesSharePageSettings(share.pageSettings, currentPageSettings),
    )
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]

  if (existingShare) {
    const linkType = type === ShareType.HUB ? 'hubs' : 'share'
    return {
      contentId,
      expiresAt: format(new Date(existingShare.expiresAt), 'yyyy-MM-dd'),
      link: `${config.contentURL}/${linkType}/${existingShare.token}`,
      id: existingShare.id,
      title,
      type,
      customValues,
      notificationScope,
    }
  }
  const expirationOverride = localStorage.getItem('shareExpirationMins')
  const user = currentUserORM(store.getState())
  const tenantHubShareExpirationDays = user?.meta.relations?.tenant?.config.hubsConfig.shareExpirationDays
  // 30 days is the default not unless configured differently for hubs
  const expirationDays = (type === ShareType.HUB && tenantHubShareExpirationDays) ? tenantHubShareExpirationDays : 30
  const expiration = expirationOverride
    ? parseInt(expirationOverride, 10)
    : expirationDays * 24 * 60;
  const params: GenerateContentShareMutationVariables = {
    // [AMPLIFY-TODO] - Case the DataStore model to the GQL input, should be the same enum/values
    type: type as any as SHARE_TYPE,
    contentId,
    expiresInMins: expiration,
    meetingId,
    customValues: customValues as any,
    customDeckDependencies,
    notificationScope: notificationScope as any,
    pageSettings: currentPageSettings as any,
  }
  const client = generateClient<GenerateContentShareMutation>();

  const { data } = await client.graphql(
    {
      query: generateContentShare,
      variables: params,
    },
  )
  const { expiresAt, id, token } = data.generateContentShare;
  const expiresAtFormatted = format(new Date(expiresAt), 'yyyy-MM-dd')
  const linkType = type === ShareType.HUB ? 'hubs' : 'share'

  store.dispatch(contentShareActions.upsert(data.generateContentShare as ContentShare))

  return {
    contentId,
    expiresAt: expiresAtFormatted,
    link: `${config.contentURL}/${linkType}/${token}`,
    id,
    title,
    type,
    customValues,
    notificationScope,
  };
}

export {
  getShareableLink,
}
